import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useChat } from '@ai-sdk/react'
import { DefaultChatTransport } from 'ai'
import clsx from 'clsx'
import Spinner from '@tandem/ui/Spinner'
import { useTRPC } from '@/trpc'
import { useAuth } from '@/auth/AuthContext'
import $layout from '@tandem/ui/layout.css'
import $type from '@tandem/ui/type.css'
import $table from '@tandem/ui/table.css'
import $form from '@tandem/ui/form.css'
import $ from './-Chat.css'

export const Route = createFileRoute('/proexpert/query')({
  component: QueryPage,
})

let cell = (v: unknown): string =>
  v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)

function ResultTable({ columns, rows }: { columns: string[]; rows: Record<string, unknown>[] }) {
  if (!rows.length) return <p class={$type.muted}>Ничего не найдено.</p>
  return (
    <div class={$table.scroller}>
      <table class={$table.table}>
        <thead><tr>{columns.map(c => <th key={c}>{c}</th>)}</tr></thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>{columns.map(c => <td key={c}>{cell(row[c])}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ToolStep({ name, part }: { name: string; part: any }) {
  let done = part.state === 'output-available'
  let out = part.output
  return (
    <div class={$.tool}>
      {name === 'run_sql' ?
        <>
          <div class={$type.muted}>▸ SQL{!done && <> <Spinner /></>}</div>
          {part.input?.sql && <pre class={$.sql}>{part.input.sql}</pre>}
          {done && (out.ok ?
            <>
              <div class={$type.muted}>{out.rowCount} строк</div>
              <ResultTable columns={out.columns} rows={out.rows} />
            </>
          : <p class={$type.error}>Ошибка: {out.error}</p>)}
        </>
      :
        <div class={$type.muted}>
          ▸ поиск сущностей: «{part.input?.text ?? ''}»{!done && <> <Spinner /></>}
          {done && ` — ${Array.isArray(out) ? out.length : 0} кандидатов`}
        </div>
      }
    </div>
  )
}

function RulesPanel() {
  let trpc = useTRPC()
  let { data: saved } = useQuery(trpc.proexpert.query.rules.get.queryOptions())
  let save = useMutation(trpc.proexpert.query.rules.set.mutationOptions())
  let [text, setText] = useState('')
  let [open, setOpen] = useState(false)
  useEffect(() => { if (saved != null) setText(saved) }, [saved])
  let dirty = saved != null && text !== saved

  return (
    <details class={$.rules} open={open} onToggle={e => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary class={$type.muted}>Бизнес-правила {saved ? '✓' : ''}</summary>
      <p class={$type.footnote}>Дополняют системный промпт ассистента: определения, политики, что считать риском, какой реестр предпочитать.</p>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder='Например: контрагент считается проблемным, если у него аннулирована хотя бы одна лицензия. Под «лицензией» по умолчанию понимай реестр ИИИ.'
        rows={6}
      />
      <button
        class={$form.button}
        disabled={!dirty || save.isPending}
        onClick={() => save.mutate({ rules: text })}
      >
        {save.isPending ? <Spinner /> : 'Сохранить'}
      </button>
    </details>
  )
}

function QueryPage() {
  let { company } = useAuth()
  let enabled = company?.features?.includes('proexpert') ?? false
  let isAdmin = company?.role === 'admin'
  let companyId = company?.company_id
  let [assistantSessionId] = useState(() => crypto.randomUUID())
  let transport = useMemo(() => new DefaultChatTransport({
    api: `${env.API_URL}/assistant/proexpert/chat`,
    credentials: 'include',
    headers: companyId ? { 'x-company-id': String(companyId) } : undefined,
  }), [companyId])
  let { messages, sendMessage, status, stop, error } = useChat({ id: `proexpert-chat-${assistantSessionId}`, transport })
  let [input, setInput] = useState('')
  let busy = status === 'submitted' || status === 'streaming'

  if (!enabled) return (
    <div class={$layout.container}><p class={$type.muted}>Раздел недоступен.</p></div>
  )

  let submit = (e: FormEvent) => {
    e.preventDefault()
    let t = input.trim()
    if (t && !busy) { sendMessage({ text: t }); setInput('') }
  }

  return (
    <div class={clsx($layout.container, $.chat)}>
      <h3>Запрос на естественном языке</h3>

      {isAdmin && <RulesPanel />}

      <div class={$.log}>
        {messages.map(m => (
          <div key={m.id} class={m.role === 'user' ? $.user : $.assistant}>
            {m.parts.map((part, i) =>
              part.type === 'text' ? <span key={i}>{part.text}</span>
              : part.type.startsWith('tool-') ? <ToolStep key={i} name={part.type.slice(5)} part={part} />
              : null
            )}
          </div>
        ))}
        {status === 'submitted' && <div class={$type.muted}><Spinner /></div>}
        {error && <p class={$type.error}>Ошибка: {error.message}</p>}
      </div>

      <form onSubmit={submit} class={$.form}>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) submit(e) }}
          placeholder='Например: у кого приостановлена лицензия?'
          rows={2}
        />
        {busy
          ? <button type='button' class={$form.button} onClick={stop}>Стоп</button>
          : <button class={$form.button} disabled={!input.trim()}>Спросить</button>}
      </form>
    </div>
  )
}
